import { useAtomValue } from 'jotai';
import { useMemo } from 'react';
import { RendererType } from '../../../../../data-models/field-definition';
import { FieldEntity, IField, isNumericRenderer } from '../../../../../data-models/field2.data-model';
import { CustomTemplateSectionType } from '../../../../../schemas/kpi-template.schema';
import { isTextualKpiField, kpiConfigAtom } from '../../../../../services/state/KPIConfigState';

function isCustomKpi(kpi: IField<unknown>): boolean {
  return kpi.entity === FieldEntity.userKPI;
}

function getRendererType(kpi: IField<unknown>): string | undefined {
  return kpi.formMeta?.renderer?.type;
}

export function isQuantitativeKpi(kpi: IField<unknown>): boolean {
  const rendererType = getRendererType(kpi) as RendererType;
  return isNumericRenderer(rendererType);
}

export function isFileKpi(kpi: IField<unknown>): boolean {
  const rendererType = getRendererType(kpi);
  return rendererType === RendererType.fileUpload || rendererType === RendererType.fileUpload2;
}

export function isDateKpi(kpi: IField<unknown>): boolean {
  return getRendererType(kpi) === RendererType.date;
}

export function matchesKpiSectionType(kpi: IField<unknown>, sectionType: CustomTemplateSectionType): boolean {
  switch (sectionType) {
    case CustomTemplateSectionType.quantitative:
      return isQuantitativeKpi(kpi);
    case CustomTemplateSectionType.textual:
      return isTextualKpiField(kpi);
    case CustomTemplateSectionType.file:
      return isFileKpi(kpi);
    case CustomTemplateSectionType.date:
      return isDateKpi(kpi);
  }
}

function useCustomKpiConfigs() {
  const kpiConfigs = useAtomValue(kpiConfigAtom);
  return useMemo(() => kpiConfigs.filter(isCustomKpi), [kpiConfigs]);
}

export function useQuantitativeCustomKpis(): IField<unknown>[] {
  const customKpis = useCustomKpiConfigs();
  return useMemo(() => customKpis.filter(isQuantitativeKpi), [customKpis]);
}

export function useTextualCustomKpis(): IField<unknown>[] {
  const customKpis = useCustomKpiConfigs();
  return useMemo(() => customKpis.filter(isTextualKpiField), [customKpis]);
}

export function useFileCustomKpis(): IField<unknown>[] {
  const customKpis = useCustomKpiConfigs();
  return useMemo(() => customKpis.filter(isFileKpi), [customKpis]);
}

export function useDateCustomKpis(): IField<unknown>[] {
  const customKpis = useCustomKpiConfigs();
  return useMemo(() => customKpis.filter(isDateKpi), [customKpis]);
}
